import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 1.5rem 4rem;
`;

export const Headline = styled.h1`
  font-family: 'Abel', sans-serif;
  font-weight: 600;
  font-size: clamp(2rem, 4vw, 3rem);
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 1rem;
`;

export const FormCard = styled.form`
  margin-top: 2rem;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const AccordionItem = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child { border-bottom: none; }
`;

export const AccordionHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 1.1rem 0.25rem;
  font-family: 'Abel', sans-serif;
  font-size: 1.05rem;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: color 0.15s ease;
  &:hover { color: ${({ theme }) => theme.colors.accent}; }
`;

export const AccordionChevron = styled.span<{ open: boolean }>`
  display: inline-block;
  font-size: 1.5rem;
  line-height: 1;
  transform: rotate(${(p) => (p.open ? '90deg' : '0deg')});
  transition: transform 0.2s ease;
  color: ${({ theme }) => theme.colors.accent};
`;

export const AccordionBody = styled.div`
  padding: 0.5rem 0.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const Label = styled.label`
  font-family: 'Abel', sans-serif;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const inputBase = `
  font-family: 'Abel', sans-serif;
  font-size: 1rem;
  border-radius: 4px;
  padding: 0.75rem 0.9rem;
  background: #fff;
  transition: border-color 0.15s ease;
`;

export const Input = styled.input`
  ${inputBase}
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;
export const Select = styled.select`
  ${inputBase}
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;
export const Textarea = styled.textarea`
  ${inputBase}
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  resize: vertical;
  min-height: 90px;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

export const CheckboxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

export const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

export const UploadBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const UploadLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1rem;
  border: 1px dashed ${({ theme }) => theme.colors.accent};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.accent};
  font-family: 'Abel', sans-serif;
  cursor: pointer;
  font-size: 0.95rem;
  background: ${({ theme }) => theme.colors.background.secondary};
  transition: background 0.15s ease;
  &:hover { background: ${({ theme }) => theme.colors.background.tertiary}; }
  input { display: none; }
`;

export const UploadHint = styled.span`
  font-family: 'Abel', sans-serif;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ThumbGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
`;

export const Thumb = styled.div`
  position: relative;
  aspect-ratio: 1;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 4px;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

export const ThumbRemove = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 1rem;
  line-height: 1;
  cursor: pointer;
`;

export const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.colors.accent};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.9rem 2rem;
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { background: #a07f3a; transform: translateY(-1px); }
`;

export const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-family: 'Abel', sans-serif;
  font-size: 0.95rem;
  background: #fff0f0;
  border: 1px solid #f5c2c2;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`;

export const SuccessBox = styled.div`
  text-align: center;
  padding: 4rem 1.5rem;
  max-width: 600px;
  margin: 0 auto;
`;

export const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  color: #fff;
  font-size: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
`;

export const SuccessTitle = styled.h2`
  font-family: 'Abel', sans-serif;
  font-weight: 600;
  letter-spacing: 0.04em;
  font-size: 2rem;
  margin: 0 0 1rem;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SuccessText = styled.p`
  font-family: 'Abel', sans-serif;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 1.1rem;
  margin: 0 0 2rem;
`;
